import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined"
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined"
import CircularProgress from "@mui/material/CircularProgress"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import { useEffect, useRef, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Row from "../../../common/components/Row"

export default function VoicemailRecordingPlayer({ voicemailId }) {
  const axios = useAuthorizedAxiosClient()
  const audioRef = useRef(null)
  const objectUrlRef = useRef(null)
  const [audioSrc, setAudioSrc] = useState(null)
  const [isLoading, setIsLoading] = useState(Boolean(voicemailId))
  const [isPlaying, setIsPlaying] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    setIsPlaying(false)
    setError(null)

    if (!voicemailId) {
      setAudioSrc(null)
      setIsLoading(false)
      return undefined
    }

    let cancelled = false
    setIsLoading(true)
    setAudioSrc(null)

    async function loadRecording() {
      try {
        const { data } = await axios.get(
          `/crm/api/inbound-voicemail/inbox/${voicemailId}/recording/`,
          { responseType: "blob" }
        )
        if (cancelled) return
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current)
        }
        const objectUrl = URL.createObjectURL(data)
        objectUrlRef.current = objectUrl
        setAudioSrc(objectUrl)
      } catch (_) {
        if (!cancelled) {
          setError("Could not load voicemail recording.")
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    loadRecording()

    return () => {
      cancelled = true
    }
  }, [axios, voicemailId])

  useEffect(
    () => () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    },
    []
  )

  async function handleTogglePlayback() {
    const audio = audioRef.current
    if (!audio || !audioSrc) {
      return
    }
    if (isPlaying) {
      audio.pause()
      return
    }
    try {
      await audio.play()
    } catch (_) {
      setError("Playback failed. Click play to try again.")
      setIsPlaying(false)
    }
  }

  if (isLoading) {
    return (
      <Row sx={{ alignItems: "center", gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Loading recording…
        </Typography>
      </Row>
    )
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    )
  }

  if (!audioSrc) {
    return null
  }

  return (
    <Row sx={{ alignItems: "center", gap: 1 }}>
      <IconButton
        aria-label={isPlaying ? "Pause voicemail" : "Play voicemail"}
        onClick={handleTogglePlayback}
        size="small"
        sx={(theme) => ({
          backgroundColor: theme.palette.primary.main,
          color: "white",
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
        })}
      >
        {isPlaying ? (
          <PauseOutlinedIcon fontSize="small" />
        ) : (
          <PlayArrowOutlinedIcon fontSize="small" />
        )}
      </IconButton>
      <Typography variant="body2" color="text.secondary">
        {isPlaying ? "Playing…" : "Play recording"}
      </Typography>
      <audio
        ref={audioRef}
        src={audioSrc}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        style={{ display: "none" }}
      />
    </Row>
  )
}
